// ─────────────────────────────────────────────────────────────────────────────
// Sync Service - TypeScript Implementation
// Background synchronization service using outbox pattern for eventual consistency
// Uses React Query hook for API integration with proper error handling and retries
// ─────────────────────────────────────────────────────────────────────────────

import {
  peekOutbox,
  removeFromOutbox,
  bumpAttempt,
  getOutboxStats,
  getSedationSyncStatus as getOutboxSedationSyncStatus,
} from "./outbox";
import { OutboxRecord, SedationCompletionPayload, EmailOutboxPayload } from "../types";
import { CompleteSedationBackup } from "../types/backupTypes";
import networkService from '../hooks/services/networkService';
import { productionLogger, logSyncStart, logSyncSuccess, logSyncError, logAuthError, logNetworkError } from '../services/productionLoggingService';
import { BackupStorageIntegrationService, BackupAssets } from '../services/backupStorageIntegrationService';
import { getBlobStorageService } from '../services/blobStorageService';
import { createCleanBackupPayload } from '../utils/backupUrlIntegration';
import { saveBackupUrlsForSync } from '../utils/backupUrlIntegration';

// ─────────────────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS FOR OFFLINE BLOB PROCESSING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract blob assets from backup data for offline processing
 * This function scans backup data for base64 images and PDF blobs that need to be uploaded
 */
async function extractBlobAssetsFromBackupData(
  backupData: CompleteSedationBackup,
  patientId: string,
  sedationId: string
): Promise<{
  hasBlobs: boolean;
  assetCount: number;
  assets: BackupAssets;
}> {
  const assets: BackupAssets = {};
  let assetCount = 0;

  try {
    // Check for PDF report blob in SedationReportReceipt
    // Note: Looking for base64 PDF data in various possible locations
    if (backupData.SedationReportReceipt) {
      const reportData = JSON.stringify(backupData.SedationReportReceipt);
      
      // Look for base64 PDF data patterns
      const pdfPattern = /data:application\/pdf[^"]+/g;
      const pdfMatches = reportData.match(pdfPattern);
      
      if (pdfMatches && pdfMatches.length > 0) {
        try {
          const base64Data = pdfMatches[0].split(',')[1];
          const pdfBlob = new Blob([Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))], { type: 'application/pdf' });
          assets.pdfBlob = pdfBlob;
          assets.pdfFilename = `sedation-report-${sedationId}.pdf`;
          assetCount++;
        } catch (error) {
          console.warn('⚠️ Failed to convert PDF base64 data:', error);
        }
      }
    }

    // Check for image blobs in session data and other locations
    const imageBlobs: Array<{ blob: Blob; hourNumber: number; index: number }> = [];
    
    // Scan IntraOppState.SessionData for base64 images
    if (backupData.IntraOppState?.SessionData) {
      const sessionData = backupData.IntraOppState.SessionData;
      
      // Look for image data in session (this may vary based on your data structure)
      if (typeof sessionData === 'object') {
        const sessionStr = JSON.stringify(sessionData);
        const base64Matches = sessionStr.match(/data:image\/[^;]+;base64,[^"]+/g);
        
        if (base64Matches) {
          base64Matches.forEach((base64Data, index) => {
            try {
              const [header, data] = base64Data.split(',');
              const mimeType = header.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
              const imageBlob = new Blob([Uint8Array.from(atob(data), c => c.charCodeAt(0))], { type: mimeType });
              
              imageBlobs.push({
                blob: imageBlob,
                hourNumber: Math.floor(index / 10) + 1, // Estimate hour based on index
                index: index % 10
              });
              assetCount++;
            } catch (error) {
              console.warn(`⚠️ Failed to convert base64 image ${index}:`, error);
            }
          });
        }
      }
    }

    // Check for base64 images in measurements or other fields
    if (backupData.SedationMeasurements) {
      backupData.SedationMeasurements.forEach((measurement, measurementIndex) => {
        const measurementStr = JSON.stringify(measurement);
        const base64Matches = measurementStr.match(/data:image\/[^;]+;base64,[^"]+/g);
        
        if (base64Matches) {
          base64Matches.forEach((base64Data, index) => {
            try {
              const [header, data] = base64Data.split(',');
              const mimeType = header.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
              const imageBlob = new Blob([Uint8Array.from(atob(data), c => c.charCodeAt(0))], { type: mimeType });
              
              imageBlobs.push({
                blob: imageBlob,
                hourNumber: measurementIndex + 1,
                index: index
              });
              assetCount++;
            } catch (error) {
              console.warn(`⚠️ Failed to convert measurement image ${measurementIndex}-${index}:`, error);
            }
          });
        }
      });
    }

    if (imageBlobs.length > 0) {
      assets.imageBlobs = imageBlobs;
    }

    // Always include sedation data for backup
    assets.sedationData = backupData as any;

    return {
      hasBlobs: assetCount > 0,
      assetCount,
      assets
    };

  } catch (error) {
    console.error('❌ Error extracting blob assets from backup data:', error);
    return {
      hasBlobs: false,
      assetCount: 0,
      assets: { sedationData: backupData as any }
    };
  }
}

// Helper: fetch image blobs from IndexedDB when not embedded in backup
async function fetchImageBlobsForSedation(patientId: string, sedationId: string): Promise<Array<{ blob: Blob; hourNumber: number; index: number }>> {
  try {
    const { getSedationImagesByPatientAndSedation } = await import('../hooks/repos/sedationImageRepo');
    const images = await getSedationImagesByPatientAndSedation(patientId, sedationId).catch(() => [] as any[]);

    const result: Array<{ blob: Blob; hourNumber: number; index: number }> = [];

    const toBlob = (dataUrl: string): Blob | null => {
      try {
        const [header, data] = dataUrl.split(',');
        const mimeType = header.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
        const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0));
        return new Blob([bytes], { type: mimeType });
      } catch {
        return null;
      }
    };

    images.forEach((img: any, idx: number) => {
      const hasAzureUrl = !!img?.ImageUrl && !String(img.ImageUrl).startsWith('data:') && String(img.ImageUrl).includes('blob.core.windows.net');
      if (hasAzureUrl) return; // skip already-uploaded

      if (typeof img?.ImageData === 'string' && img.ImageData.startsWith('data:')) {
        const blob = toBlob(img.ImageData);
        if (blob) result.push({ blob, hourNumber: Math.floor(idx / 10) + 1, index: idx % 10 });
      }
    });

    return result;
  } catch (e) {
    console.warn('⚠️ Failed to fetch image blobs from repo:', e);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SYNC SERVICE CONFIGURATION AND CORE FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// Environment configuration for sync control
const SYNC_ENABLED = process.env.REACT_APP_SYNC_ENABLED !== 'false'; // Default true, set to 'false' to disable

// Configuration interface for sync service
export interface SyncConfig {
  intervalMs?: number;
  batchSize?: number;
  maxAttempts?: number;
  enableLogging?: boolean;
  enabled?: boolean; // New: can be overridden at runtime
}

// Default configuration
const DEFAULT_CONFIG: Required<SyncConfig> = {
  intervalMs: 30_000, // 30 seconds - more frequent for better UX
  batchSize: 25, // Smaller batches to prevent API overload
  maxAttempts: 10,
  enableLogging: true,
  enabled: SYNC_ENABLED,
};

// Sync statistics interface
export interface SyncStats {
  lastSync: string | null;
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  isRunning: boolean;
}

// Global sync statistics
let syncStats: SyncStats = {
  lastSync: null,
  totalProcessed: 0,
  successCount: 0,
  errorCount: 0,
  isRunning: false,
};

// Global processing lock to prevent concurrent outbox processing
let isProcessingOutbox = false;

// Axios client instance for API calls (injected from React context)
let axiosClient: any = null;

// Set the axios client instance (called from React component)
export function setAxiosClient(client: any): void {
  if (!client) return;
  axiosClient = client;
}

// Post sedation completion event using the existing API endpoint (same as useCreateSyncRequest)
export async function postSedationCompletion(
  completeSedationBackup: CompleteSedationBackup, 
  config: Required<SyncConfig>
): Promise<void> {
  if (!config.enabled) {
    if (config.enableLogging) {
      console.log('🔇 Sync disabled via configuration - skipping sedation completion sync');
    }
    return;
  }

  if (!networkService.isOnline()) {
    if (config.enableLogging) {
      console.log('🌐 Network service reports offline, blocking sync');
    }
    throw new Error('Cannot sync while offline - network unavailable');
  }

  if (!axiosClient) {
    throw new Error('Axios client not initialized - call setAxiosClient(useAxiosClient()) before starting background sync');
  }

  // CRITICAL: Check if user is authenticated before attempting sync
  // The 401 errors suggest we're trying to sync without valid authentication
  try {
    // Try to get the auth state from localStorage (where it's typically stored)
    const authStateJson = localStorage.getItem('UserAuthState');
    const authState = authStateJson ? JSON.parse(authStateJson) : null;
    
    if (!authState?.token?.token) {
      const errorMsg = 'Cannot sync - user not authenticated or token expired';
      logAuthError(new Error(errorMsg), {
        hasAuthState: !!authState,
        hasToken: !!authState?.token,
        hasTokenValue: !!authState?.token?.token,
        tokenExpiry: authState?.token?.expires
      });
      
      if (config.enableLogging) {
        console.log('🔐 Auth check failed:', {
          hasAuthState: !!authState,
          hasToken: !!authState?.token,
          hasTokenValue: !!authState?.token?.token,
          tokenExpiry: authState?.token?.expires
        });
      }
      throw new Error(errorMsg);
    }

    // Additional check for token expiry
    if (authState.token.tokenExpiration && new Date() > new Date(authState.token.tokenExpiration)) {
      const errorMsg = 'Cannot sync - authentication token has expired';
      logAuthError(new Error(errorMsg), {
        tokenExpiry: authState.token.expires,
        currentTime: new Date().toISOString(),
        isTokenExpired: true
      });
      
      if (config.enableLogging) {
        console.log('🔐 Token expired:', {
          tokenExpiry: authState.token.expires,
          currentTime: new Date().toISOString()
        });
      }
      throw new Error(errorMsg);
    }

  } catch (authError) {
    if (config.enableLogging) {
      console.log('❌ Authentication check failed:', authError);
    }
    throw authError;
  }

  try {
    // CRITICAL: Convert API payload format and fix data types
    const { convertBackupDataForAPI } = await import('../utils/apiDataConverter');
    // Ensure PdfUrl/FileType correctness prior to API submission
    let prepared = convertBackupDataForAPI(completeSedationBackup);

    // Use the same API call as the hook via shared function
    const { createSyncRequest } = await import('../api/synchronization/synchronizationService');
    
    try {
      logSyncStart({
        operation: 'sedation_completion',
        apiEndpoint: '/api/sync/AddSyncRequest',
        recordId: prepared.SedationDetail?.Id?.toString()
      });

      const response = await createSyncRequest(axiosClient, prepared as any);
      
      logSyncSuccess({
        operation: 'sedation_completion',
        recordId: prepared.SedationDetail?.Id?.toString(),
        responseStatus: response.status,
        apiEndpoint: '/api/sync/AddSyncRequest'
      });

      // Update sync statistics
      syncStats.successCount++;
      syncStats.lastSync = new Date().toISOString();

    } catch (syncError) {
      logSyncError(syncError, {
        operation: 'sedation_completion',
        recordId: prepared.SedationDetail?.Id?.toString(),
        apiEndpoint: '/api/sync/AddSyncRequest',
        responseStatus: syncError && typeof syncError === 'object' && 'response' in syncError ? syncError.response?.status : undefined
      });
      // Enhanced error logging to understand the 401 issue
      if (config.enableLogging) {
        console.log('❌ Sync API call failed:', {
          errorName: syncError instanceof Error ? syncError.name : 'Unknown',
          errorMessage: syncError instanceof Error ? syncError.message : 'Unknown error',
          isAxiosError: syncError && typeof syncError === 'object' && 'isAxiosError' in syncError,
          requestUrl: syncError && typeof syncError === 'object' && 'config' in syncError ? syncError.config?.url : 'Unknown',
          requestHeaders: syncError && typeof syncError === 'object' && 'config' in syncError ? syncError.config?.headers : null,
          responseStatus: syncError && typeof syncError === 'object' && 'response' in syncError ? syncError.response?.status : 'No response',
          responseHeaders: syncError && typeof syncError === 'object' && 'response' in syncError ? syncError.response?.headers : null
        });
      }
      throw syncError;
    }

  } catch (error) {
    syncStats.errorCount++;
    if (config.enableLogging) {
      console.error('❌ Failed to sync sedation completion:', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorName: error instanceof Error ? error.name : 'Unknown',
        isAxiosError: error && typeof error === 'object' && 'isAxiosError' in error,
        responseStatus: error && typeof error === 'object' && 'response' in error ? error.response?.status : undefined,
        responseData: error && typeof error === 'object' && 'response' in error ? error.response?.data : undefined,
        requestConfig: error && typeof error === 'object' && 'config' in error ? {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        } : undefined,
        environment: process.env.NODE_ENV,
        timestamp: new Date().toISOString()
      });
    }
    throw error;
  }
}

// Process a single batch of outbox records
export async function drainOutboxOnce(
  config: Partial<SyncConfig> = {}
): Promise<{
  processed: number;
  succeeded: number;
  failed: number;
}> {
  // PREVENT CONCURRENT PROCESSING: If already processing, skip this call
  if (isProcessingOutbox) {
    if (config.enableLogging) {
      console.log('⏭️ Outbox processing already in progress - skipping concurrent call');
    }
    return { processed: 0, succeeded: 0, failed: 0 };
  }

  // Set processing lock
  isProcessingOutbox = true;

  try {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Check if sync is enabled
  if (!finalConfig.enabled) {
    if (finalConfig.enableLogging) {
      console.log('🔇 Sync disabled - skipping outbox processing');
    }
    return { processed: 0, succeeded: 0, failed: 0 };
  }

  if (!networkService.isOnline()) {
    if (finalConfig.enableLogging) {
      console.log('🌐 Network service reports offline, skipping outbox processing');
    }
    return { processed: 0, succeeded: 0, failed: 0 };
  }

  // PRIORITY PROCESSING: Get records with sedation completion events first
  const batch = await peekOutbox(finalConfig.batchSize);

  let succeeded = 0;
  let failed = 0;

  if (finalConfig.enableLogging && batch.length > 0) {
    console.log(`🔄 Processing ${batch.length} outbox records for sync (PRIORITY: sedation completion → emails)...`);
    
    // Log priority breakdown
    const sedationEvents = batch.filter(e => e.operation === 'sedation_completed' || e.entityType === 'SedationCompletion');
    const emailEvents = batch.filter(e => e.operation === 'email_send' || e.entityType === 'Email');
    
    if (sedationEvents.length > 0) {
      console.log(`🏥 Processing ${sedationEvents.length} sedation completion events FIRST`);
    }
    if (emailEvents.length > 0) {
      console.log(`📧 Processing ${emailEvents.length} email events AFTER sedation completion`);
    }
  }

  // SEQUENTIAL PROCESSING: Process events one by one to avoid concurrency issues
  // CRITICAL: Process each event individually and WAIT for completion before moving to next
  for (let i = 0; i < batch.length; i++) {
    const evt = batch[i];
    syncStats.totalProcessed++;

    try {
      // Skip records that have exceeded max attempts
      if (evt.attempts >= finalConfig.maxAttempts) {
        if (finalConfig.enableLogging) {
          console.warn(`⚠️ Skipping record ${evt.id} - max attempts (${finalConfig.maxAttempts}) exceeded`);
        }
        failed++;
        continue;
      }

      if (finalConfig.enableLogging) {
        console.log(`🔄 Processing outbox record ${i + 1}/${batch.length}: ${evt.id} (${evt.operation}, attempt ${evt.attempts + 1}/${finalConfig.maxAttempts})`);
      }

      // Process sedation completion events
      if (evt.operation === 'sedation_completed' && evt.data) {
        const payload = evt.data as SedationCompletionPayload;
        
        // CRITICAL FIX: Validate payload has required data before processing
        if (!payload.data) {
          throw new Error('Sedation completion payload missing required data');
        }

        // CRITICAL FIX: Validate the backup data structure
        const backupData = payload.data as CompleteSedationBackup;
        if (!backupData.SedationDetail || !backupData.Patient) {
          throw new Error(`Incomplete backup data - missing ${!backupData.SedationDetail ? 'SedationDetail' : 'Patient'}`);
        }

        if (finalConfig.enableLogging) {
          console.log(`🏥 Starting sedation completion sync for: ${payload.sedationId}`);
          console.log(`📊 Backup data validation:`, {
            hasPatient: !!backupData.Patient,
            hasSedationDetail: !!backupData.SedationDetail,
            hasSessionData: !!backupData.IntraOppState?.SessionData,
            measurementCount: backupData.SedationMeasurements?.length || 0,
            hasReport: !!backupData.SedationReportReceipt
          });
        }

        // ⚡ CRITICAL FIX: OFFLINE PROCESSING MUST UPLOAD BLOBS FIRST (EXACTLY LIKE ONLINE)
        console.log('🔄 OFFLINE PROCESSING: Uploading blobs to storage before API call (matching online behavior)');
        
        let cleanBackupData: CompleteSedationBackup;
        
        try {
          // Initialize blob storage services for offline processing
          const blobStorageService = getBlobStorageService();
          const backupStorageService = new BackupStorageIntegrationService(blobStorageService);
          
          if (finalConfig.enableLogging) {
            console.log('📤 OFFLINE: Checking for base64 images/PDFs in backup data and uploading to blob storage...');
          }

          // CRITICAL: Extract blobs for upload (images, PDFs) from raw backup data
          const extractedAssets = await extractBlobAssetsFromBackupData(backupData, payload.patientId, payload.sedationId);

          // Supplement with any images from the local repo if not found in backup (parity with online ImageUploadService)
          try {
            const repoImageBlobs = await fetchImageBlobsForSedation(payload.patientId, payload.sedationId);
            if (repoImageBlobs.length > 0) {
              extractedAssets.assets.imageBlobs = [
                ...(extractedAssets.assets.imageBlobs || []),
                ...repoImageBlobs
              ];
            }
          } catch (repoImgErr) {
            console.warn('⚠️ OFFLINE: Could not supplement assets with repo images:', repoImgErr);
          }
          
          // If no PDF blob present or existing PdfUrl not an Azure URL, regenerate the PDF now
          const hasAzurePdfUrl = !!backupData.SedationDetail?.PdfUrl && backupData.SedationDetail.PdfUrl.includes('blob.core.windows.net');
          if (!extractedAssets.assets.pdfBlob && !hasAzurePdfUrl) {
            try {
              console.log('🧾 OFFLINE: No PDF found in assets; regenerating PDF for upload...');
              const { SedationPdfService } = await import('../services/sedationPdfService');
              const { SedationPdfDataService } = await import('../services/pdfDataService');
              const { SedationReportConverter } = await import('../types');

              const pdfService = new SedationPdfService();
              const dataService = new SedationPdfDataService();

              // Build report UI from stored receipt (fallback to empty if missing)
              const reportUI = backupData.SedationReportReceipt
                ? SedationReportConverter.toUI(backupData.SedationReportReceipt as any)
                : SedationReportConverter.createEmptyUI();

              // Gather model for PDF
              const pdfModel = await dataService.gatherPdfData(
                payload.patientId,
                payload.sedationId,
                reportUI,
                backupData.IntraOppState?.SessionData
              );

              // Build PDF blob (no side effects)
              const { blob: regeneratedPdfBlob, filename: regeneratedFilename } = await pdfService.buildPdfBlob({
                pdfModel,
                patientName: `${pdfModel.patient.FirstName} ${pdfModel.patient.LastName}`.trim(),
                datetimeCreated: new Date(),
                patientId: payload.patientId,
                sedationId: payload.sedationId,
                sessionData: backupData.IntraOppState?.SessionData,
                reportData: reportUI
              } as any);

              // Attach to assets for upload
              extractedAssets.assets.pdfBlob = regeneratedPdfBlob;
              extractedAssets.assets.pdfFilename = regeneratedFilename;
            } catch (regenErr) {
              console.error('❌ OFFLINE: Failed to regenerate PDF:', regenErr);
            }
          }
          
          let uploadResult: { success: boolean; pdfUrl?: string; imageUrls?: string[]; dataBackupUrl?: string; error?: string } = { success: true };

          const needsUpload = (extractedAssets.assets.imageBlobs && extractedAssets.assets.imageBlobs.length > 0) ||
                              !!extractedAssets.assets.pdfBlob ||
                              true; // always upload data backup

          if (needsUpload) {
            console.log(`📤 OFFLINE: Uploading assets (pdf:${!!extractedAssets.assets.pdfBlob}, images:${(extractedAssets.assets.imageBlobs||[]).length}, data:true)`);
            // Prefer smart upload to avoid duplicates
            uploadResult = await backupStorageService.smartUploadBackupAssets(
              payload.patientId,
              payload.sedationId,
              `${backupData.Patient?.FirstName || ''} ${backupData.Patient?.LastName || ''}`.trim() || 'Unknown Patient',
              extractedAssets.assets,
              false
            );

            // If we successfully uploaded the PDF, update local SedationDetail
            if (uploadResult.success && uploadResult.pdfUrl) {
              try {
                const { SedationDetailUpdateService } = await import('../services/sedationDetailUpdateService');
                await new SedationDetailUpdateService().updatePdfUrl(payload.sedationId, uploadResult.pdfUrl, 'URL');
                console.log('✅ OFFLINE: Updated local SedationDetail with PdfUrl');
              } catch (updErr) {
                console.warn('⚠️ OFFLINE: Failed to update local SedationDetail with PdfUrl:', updErr);
              }
            }

            // Persist backup URLs like online flow for later clean payload creation
            try {
              await saveBackupUrlsForSync(
                payload.patientId,
                payload.sedationId,
                `${backupData.Patient?.FirstName || ''} ${backupData.Patient?.LastName || ''}`.trim() || 'Unknown Patient',
                {
                  pdfUrl: uploadResult.pdfUrl,
                  imageUrls: uploadResult.imageUrls || [],
                  dataBackupUrl: uploadResult.dataBackupUrl
                }
              );
            } catch (saveUrlsErr) {
              console.warn('⚠️ OFFLINE: Failed to persist backup URLs:', saveUrlsErr);
            }
          }

          if (uploadResult.success) {
            console.log('✅ OFFLINE: Blob upload successful - creating clean payload with URLs');
            cleanBackupData = await createCleanBackupPayload(payload.sedationId, backupData);

            // CRITICAL: the clean payload's SedationDetail.PdfUrl is still null at this point
            // because createCleanBackupPayload only sets BackupMetadata.PdfUrl.
            // The backend reads SedationDetail.PdfUrl to store the link and trigger the email.
            if (uploadResult.pdfUrl && cleanBackupData.SedationDetail) {
              cleanBackupData = {
                ...cleanBackupData,
                SedationDetail: {
                  ...cleanBackupData.SedationDetail,
                  PdfUrl: uploadResult.pdfUrl,
                  FileType: 'URL' as any,
                }
              };
              console.log('✅ OFFLINE: SedationDetail.PdfUrl set from upload result:', uploadResult.pdfUrl);
            }
            if (finalConfig.enableLogging) {
              console.log('🧹 OFFLINE: Clean backup data created with URLs:', {
                pdfUrl: uploadResult.pdfUrl,
                imageCount: uploadResult.imageUrls?.length || 0,
                dataBackupUrl: uploadResult.dataBackupUrl
              });
            }
          } else if (extractedAssets.hasBlobs) {
            throw new Error(`OFFLINE blob upload failed: ${uploadResult.error}`);
          } else {
            console.log('ℹ️ OFFLINE: No blob data uploaded - creating clean payload anyway');
            cleanBackupData = await createCleanBackupPayload(payload.sedationId, backupData);
          }
          
          if (finalConfig.enableLogging) {
            console.log('🎯 OFFLINE: Processing complete - clean payload ready for API (0% blob data)');
          }
          
        } catch (blobError) {
          console.error('❌ OFFLINE: Blob storage upload failed, attempting with raw data:', blobError);
          
          // Fallback: try to create clean payload without blob uploads
          try {
            cleanBackupData = await createCleanBackupPayload(payload.sedationId, backupData);
            console.log('⚠️ OFFLINE: Using fallback clean payload (may have missing URLs)');
          } catch (cleanError) {
            console.error('❌ OFFLINE: Clean payload creation failed, using raw data as last resort:', cleanError);
            cleanBackupData = backupData;
          }
        }
        
        // AWAIT the completion to ensure it finishes before processing next event
        // NOW using cleanBackupData with URLs instead of raw blobs (EXACTLY like online)
        await postSedationCompletion(cleanBackupData, finalConfig);
        await removeFromOutbox(evt.id);
        succeeded++;
        
        if (finalConfig.enableLogging) {
          console.log(`✅ Synced sedation completion: ${payload.sedationId}`);
        }

        // Log successful sedation sync to production logger
        productionLogger.info('Sedation completion synced successfully', {
          context: 'sedation_sync_success',
          patientId: payload.patientId,
          sedationId: payload.sedationId,
          outboxRecordId: evt.id,
          attempts: evt.attempts + 1
        });
      } else if (evt.operation === 'email_send' && evt.data) {
        const payload = evt.data as EmailOutboxPayload;
        
        if (finalConfig.enableLogging) {
          console.log(`📧 Starting email send for sedation: ${payload.sedationId}`);
        }
        
        try {
          // Import email service dynamically to avoid circular dependencies
          const { emailService } = await import('../services/emailService');
          
          // AWAIT the email sending to ensure it finishes before processing next event
          const response = await emailService.sendEmail(payload.emailMessage);
          
          if (response.success) {
            await removeFromOutbox(evt.id);
            succeeded++;
            
            if (finalConfig.enableLogging) {
              console.log(`✅ Sent queued email for sedation: ${payload.sedationId}`, {
                recipients: payload.emailMessage.To.length,
                messageId: response.messageId
              });
            }

            // Log successful email sync to production logger
            productionLogger.info('Queued email sent successfully', {
              context: 'email_sync_success',
              patientId: payload.patientId,
              sedationId: payload.sedationId,
              recipientCount: payload.emailMessage.To.length,
              messageId: response.messageId,
              outboxRecordId: evt.id,
              attempts: evt.attempts + 1
            });
          } else {
            throw new Error(response.error || 'Email sending failed');
          }
        } catch (emailError) {
          // Log email sync error to production logger
          productionLogger.error('Failed to send queued email', {
            context: 'email_sync_error',
            patientId: payload.patientId,
            sedationId: payload.sedationId,
            errorDetails: {
              name: emailError instanceof Error ? emailError.name : 'Unknown',
              message: emailError instanceof Error ? emailError.message : 'Unknown error'
            },
            outboxRecordId: evt.id,
            attempts: evt.attempts + 1
          });
          
          // Re-throw to trigger normal retry logic
          throw emailError;
        }
      } 
      else {
        // Handle other event types if needed in the future
        if (finalConfig.enableLogging) {
          console.warn(`⚠️ Unsupported event type: ${evt.operation} - skipping`);
        }
        failed++;
      }

    } catch (error) {
      // Don't burn retry attempts on transient offline or auth errors — only count real server errors
      const errMsg = error instanceof Error ? error.message : '';
      const httpStatus = (error as any)?.response?.status;
      const isOfflineError = errMsg.includes('network unavailable') || errMsg.includes('Network Error') ||
        (error as any)?.code === 'ECONNREFUSED' || (error as any)?.code === 'ENOTFOUND';
      const isAuthError = errMsg.includes('not authenticated') || errMsg.includes('token expired') ||
        httpStatus === 401 || httpStatus === 403;

      if (!isOfflineError && !isAuthError) {
        await bumpAttempt(evt.id);
      }
      failed++;
      syncStats.errorCount++;

      if (finalConfig.enableLogging) {
        console.error(`❌ Failed to sync record ${evt.id} (attempt ${evt.attempts + 1}/${finalConfig.maxAttempts}):`, error);
        console.log(`🔄 Continuing with remaining ${batch.length - (succeeded + failed)} events...`);
      }

      // Log individual record failure but continue processing
      productionLogger.error('Outbox record sync failed', {
        context: 'outbox_record_sync_error',
        recordId: evt.id,
        operation: evt.operation,
        entityType: evt.entityType,
        attempts: evt.attempts + 1,
        maxAttempts: finalConfig.maxAttempts,
        errorDetails: {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : 'Unknown error'
        }
      });

      // If this was the max attempt, log it as permanently failed
      if (evt.attempts + 1 >= finalConfig.maxAttempts) {
        console.error(`💀 Record ${evt.id} permanently failed after ${finalConfig.maxAttempts} attempts`);
        
        productionLogger.error('Outbox record permanently failed', {
          context: 'outbox_record_permanent_failure',
          recordId: evt.id,
          operation: evt.operation,
          entityType: evt.entityType,
          finalAttempts: finalConfig.maxAttempts
        });
      }
      
      // IMPORTANT: Do not break or return here - continue processing remaining events
    }
  }

  if (batch.length > 0) {
    syncStats.lastSync = new Date().toISOString();

    if (finalConfig.enableLogging) {
      console.log(
        `✅ Batch sync complete: ${succeeded} succeeded, ${failed} failed`
      );
    }
  }

  return {
    processed: batch.length,
    succeeded,
    failed,
  };
  } finally {
    // CRITICAL: Always release the processing lock
    isProcessingOutbox = false;
  }
}

// Background sync service management
let syncTimer: NodeJS.Timeout | null = null;
let networkUnsubscribe: (() => void) | null = null;

// Start background synchronization
export function startBackgroundSync(
  config: Partial<SyncConfig> = {}
): () => void {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Stop any existing sync first
  stopBackgroundSync();

  syncStats.isRunning = true;

  if (finalConfig.enableLogging) {
    console.log(
      `Starting background sync with ${finalConfig.intervalMs}ms interval`
    );
  }

  // Initial sync attempt - let drainOutboxOnce handle network checks
  drainOutboxOnce(finalConfig).catch((error) => {
    if (finalConfig.enableLogging) {
      console.error("Initial sync failed:", error);
    }
  });

  // Set up periodic sync - let drainOutboxOnce handle network checks
  syncTimer = setInterval(() => {
    drainOutboxOnce(finalConfig).catch((error) => {
      if (finalConfig.enableLogging) {
        console.error("Periodic sync failed:", error);
      }
    });
  }, finalConfig.intervalMs);

  // Set up network event handler using networkService for better reliability
  networkUnsubscribe = networkService.onOnline(() => {
    if (finalConfig.enableLogging) {
      console.log("Device came online - triggering sync");
    }
    drainOutboxOnce(finalConfig).catch((error) => {
      console.error("Online sync failed:", error);
    });
  });

  // Return cleanup function
  return stopBackgroundSync;
}

// Stop background synchronization
export function stopBackgroundSync(): void {
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }

  if (networkUnsubscribe) {
    networkUnsubscribe();
    networkUnsubscribe = null;
  }

  syncStats.isRunning = false;
}

// Get current sync statistics
export function getSyncStats(): SyncStats & {
  outboxStats?: Awaited<ReturnType<typeof getOutboxStats>>;
} {
  return { ...syncStats };
}

// Get detailed sync information including outbox stats
export async function getDetailedSyncStats(): Promise<
  SyncStats & { outboxStats: Awaited<ReturnType<typeof getOutboxStats>> }
> {
  const outboxStats = await getOutboxStats();
  return {
    ...syncStats,
    outboxStats,
  };
}

// Reset sync statistics
export function resetSyncStats(): void {
  syncStats = {
    lastSync: null,
    totalProcessed: 0,
    successCount: 0,
    errorCount: 0,
    isRunning: syncStats.isRunning, // Preserve running state
  };

  console.log("Sync statistics reset");
}

// Force immediate sync attempt
export async function forceSyncNow(config: Partial<SyncConfig> = {}): Promise<{
  processed: number;
  succeeded: number;
  failed: number;
}> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (finalConfig.enableLogging) {
    console.log("Forcing immediate sync...");
  }

  // Let drainOutboxOnce handle network checks (includes production bypass)
  return await drainOutboxOnce(finalConfig);
}

// Check if sync service is healthy
export async function checkSyncHealth(): Promise<{
  isHealthy: boolean;
  issues: string[];
  recommendations: string[];
}> {
  const result = {
    isHealthy: true,
    issues: [] as string[],
    recommendations: [] as string[],
  };

  try {
    const outboxStats = await getOutboxStats();

    // Check for excessive failed records
    if (outboxStats.failed > 10) {
      result.isHealthy = false;
      result.issues.push(
        `${outboxStats.failed} records have failed multiple sync attempts`
      );
      result.recommendations.push(
        "Review failed records and consider manual intervention"
      );
    }

    // Check for old pending records
    if (outboxStats.oldestRecord && outboxStats.total > 0) {
      const age =
        Date.now() - new Date(outboxStats.oldestRecord.enqueuedAt).getTime();
      const hoursOld = age / (1000 * 60 * 60);

      if (hoursOld > 24) {
        result.isHealthy = false;
        result.issues.push(
          `Oldest outbox record is ${hoursOld.toFixed(1)} hours old`
        );
        result.recommendations.push(
          "Check network connectivity and backend API availability"
        );
      }
    }

    // Check sync error rate
    if (syncStats.totalProcessed > 0) {
      const errorRate = syncStats.errorCount / syncStats.totalProcessed;
      if (errorRate > 0.1) {
        // More than 10% error rate
        result.isHealthy = false;
        result.issues.push(
          `High sync error rate: ${(errorRate * 100).toFixed(1)}%`
        );
        result.recommendations.push("Check backend API health and error logs");
      }
    }

    // Check if sync is running
    if (!syncStats.isRunning && outboxStats.total > 0) {
      result.issues.push(
        "Background sync is not running but outbox has pending records"
      );
      result.recommendations.push("Start background sync service");
    }
  } catch (error) {
    result.isHealthy = false;
    result.issues.push(
      `Failed to check sync health: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }

  return result;
}

// Add convenience method for sedation completion sync status
export async function getSedationSyncStatus(): Promise<{
  pendingSedationEvents: number;
  lastSedationSync: string | null;
}> {
  const allRecords = await peekOutbox(1000);
  
  const sedationEvents = allRecords.filter(record => 
    record.operation === 'sedation_completed' || 
    record.entityType === 'SedationCompletion'
  );

  const lastSedationEvent = sedationEvents
    .sort((a, b) => new Date(b.enqueuedAt).getTime() - new Date(a.enqueuedAt).getTime())[0];

  return {
    pendingSedationEvents: sedationEvents.length,
    lastSedationSync: lastSedationEvent?.enqueuedAt || null
  };
}
